import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparklesIcon, CarIcon, ShieldCheckIcon, PaintbrushIcon, WrenchIcon } from 'lucide-react';
type ServiceItem = {
  title: string;
  description: string;
  price: string;
  link: string;
  icon: React.ReactNode;
};
const serviceItems: ServiceItem[] = [{
  title: 'Interior Detail',
  description: 'Deep clean, shampoo, and steam sanitize every surface inside your vehicle. Serving Camarillo, Thousand Oaks, and all of Ventura County with premium interior detailing at your door.',
  price: 'Starting at $180',
  link: '/services/interior-detailing',
  icon: <SparklesIcon className="w-7 h-7" data-id="element-400" data-mp="2tfxvfzwuuclt" />
}, {
  title: 'Exterior Detail',
  description: 'Hand wash, clay bar treatment, and paint protection for a showroom-quality finish. Professional mobile exterior detailing throughout Ventura County and the San Fernando Valley.',
  price: 'Starting at $160',
  link: '/services/exterior-detailing',
  icon: <CarIcon className="w-7 h-7" data-id="element-401" data-mp="31ekwexy3brc6" />
}, {
  title: 'Full Car Detail',
  description: 'Our most popular package — complete interior and exterior restoration with hybrid ceramic wax. The best full mobile car detail in Camarillo, Simi Valley, and Ventura County.',
  price: 'Starting at $300',
  link: '/services/full-car-detail',
  icon: <ShieldCheckIcon className="w-7 h-7" data-id="element-402" data-mp="35fekpsfg385l" />
}, {
  title: 'Hybrid Ceramic Wax',
  description: 'Long-lasting ceramic protection that shields your paint from UV rays, dirt, and contaminants. Professional-coating applied at your home or office in Ventura County.',
  price: 'Call for Quote',
  link: '/services/ceramic-coating',
  icon: <PaintbrushIcon className="w-7 h-7" data-id="element-403" data-mp="3t58v83cakkmo" />
}, {
  title: 'Headlight Restoration',
  description: 'Restore cloudy, yellowed headlights to crystal-clear condition for improved visibility and appearance. Mobile headlight restoration serving Calabasas, Woodland Hills, and Ventura County.',
  price: 'From $80',
  link: '/services/headlight-restoration',
  icon: <WrenchIcon className="w-7 h-7" data-id="element-404" data-mp="2vsi9m1skkv4g" />
}];
export function ServiceGrid() {
  return <section className="w-full bg-black py-16 px-4 border-t border-zinc-900" id="service-details" data-id="element-405" data-mp="2zt15c6xl2lbx">
      <div className="max-w-6xl mx-auto" data-id="element-406" data-mp="34sniyhhbq3l9">
        <div className="text-center mb-12" data-id="element-407" data-mp="34rlw7fqy3snp">
          <h2 className="text-5xl md:text-6xl font-black uppercase mb-4" data-id="element-408" data-mp="39j3ufw1l3f4z">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-409" data-mp="39kf1q5u8n50u">
              Our Detailing Services
            </span>
          </h2>
          <div className="h-1 w-24 bg-red-600 mx-auto rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-410" data-mp="3jfgo4tx4pszq" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mt-4" data-id="element-411" data-mp="3ngsgv8bauuc5">
            Professional mobile auto detailing services delivered to your
            driveway in Ventura County and the San Fernando Valley
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-412" data-mp="1y3f733kf4e3n">
          {serviceItems.map((service, index) => <motion.div key={index} initial={{
          y: 40,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} className="group relative flex flex-col bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden hover:border-red-900/60 transition-all duration-300" data-id="element-413" data-mp="1qlhdk8imtdm2" data-mpi={index}>
              {/* Card Header */}
              <div className="p-6 pb-4" data-id="element-414" data-mp="24kgt8qo62i63">
                <div className="flex items-center justify-between mb-3" data-id="element-415" data-mp="24kqi2axh6ja7">
                  <div className="p-2.5 bg-red-600/10 border border-red-900/30 rounded-lg text-red-500" data-id="element-416" data-mp="1zkcjdjhrklou">
                    {service.icon}
                  </div>
                  <span className="inline-flex items-center px-3 py-1 bg-zinc-800 border border-zinc-700 rounded-full text-sm font-bold text-yellow-400 tracking-wide" data-id="element-417" data-mp="o5yzsqu1msem">
                    {service.price}
                  </span>
                </div>
                <h3 className="text-2xl font-black text-white uppercase tracking-wide mt-2" data-id="element-418" data-mp="2809wbny37lyx">
                  {service.title}
                </h3>
              </div>

              {/* Description */}
              <div className="px-6 pb-4 flex-1" data-id="element-419" data-mp="1y46t0eri1hae">
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-420" data-mp="231yo1hnfxjit">
                  {service.description}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="p-6 pt-2 flex flex-col gap-3" data-id="element-421" data-mp="2qpq9baqyl6w8">
                <Link to={service.link} className="w-full text-center py-3 px-4 border border-zinc-700 text-white font-bold text-sm uppercase tracking-wider rounded-lg hover:bg-zinc-800 hover:border-zinc-600 transition-all" data-id="element-422" data-mp="2vpbalrtlprc4">
                  Learn More
                </Link>
                <a href="#contact" className="w-full text-center py-3 px-4 bg-red-600 hover:bg-red-500 text-white font-bold text-sm uppercase tracking-wider rounded-lg transition-all shadow-[0_0_15px_rgba(220,38,38,0.3)] hover:shadow-[0_0_25px_rgba(220,38,38,0.5)]" data-id="element-423" data-mp="16bg6wx3j58yv">
                  Book Now
                </a>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}